import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import toast from "react-hot-toast";

type JoinLink = {
  id: string;
  token: string;
  label: string | null;
  expires_at: string | null;
  max_uses: number | null;
  uses: number;
  revoked_at: string | null;
  created_at: string;
};

/**
 * Share links for one team.
 *
 * The link goes in a parents' WhatsApp group, which means it will be forwarded
 * beyond it. The screen says so, and makes revoking a one-click remedy rather
 * than something to go looking for.
 */
export default function TeamJoinLinks({ teamId, teamName }: { teamId: string; teamName: string }) {
  const [links, setLinks] = useState<JoinLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [days, setDays] = useState("30");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<{ links: JoinLink[] }>(`/join-links/team/${teamId}`);
      setLinks(data.links || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-au putut încărca linkurile.");
    }
    setLoading(false);
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  const publicUrl = (token: string) => `${window.location.origin}/join/${token}`;

  const create = async () => {
    setCreating(true);
    try {
      await api.post(`/join-links/team/${teamId}`, {
        label: label.trim() || undefined,
        days: Number(days) || 30,
      });
      setLabel("");
      toast.success("Link creat.");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut crea linkul.");
    }
    setCreating(false);
  };

  const copy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(publicUrl(token));
      toast.success("Link copiat.");
    } catch {
      toast.error("Copiază manual linkul din listă.");
    }
  };

  const revoke = async (link: JoinLink) => {
    if (!window.confirm("Dezactivezi linkul? Cine îl are nu îl mai poate folosi.")) return;
    try {
      await api.delete(`/join-links/${link.id}`);
      toast.success("Link dezactivat.");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut dezactiva linkul.");
    }
  };

  const state = (l: JoinLink) => {
    if (l.revoked_at) return { label: "Dezactivat", cls: "bg-slate-100 text-slate-500" };
    if (l.expires_at && new Date(l.expires_at) < new Date())
      return { label: "Expirat", cls: "bg-slate-100 text-slate-500" };
    if (l.max_uses != null && l.uses >= l.max_uses)
      return { label: "Epuizat", cls: "bg-slate-100 text-slate-500" };
    return { label: "Activ", cls: "bg-emerald-100 text-emerald-700" };
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Trimite linkul în grupul de WhatsApp al părinților echipei <strong>{teamName}</strong>.
        Părintele își face cont și înscrie copilul, iar tu confirmi înainte ca el să apară în
        echipă.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[160px]">
          <label className="block mb-1 text-xs font-medium text-slate-600" htmlFor="jl-label">
            Etichetă <span className="text-slate-400">(opțional)</span>
          </label>
          <input
            id="jl-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="ex. grupa mică"
            className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
          />
        </div>
        <div className="w-28">
          <label className="block mb-1 text-xs font-medium text-slate-600" htmlFor="jl-days">
            Valabil (zile)
          </label>
          <input
            id="jl-days"
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
          />
        </div>
        <button
          type="button"
          onClick={create}
          disabled={creating}
          className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-lg disabled:opacity-60"
        >
          {creating ? "Se creează..." : "Creează link"}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Se încarcă...</p>
      ) : links.length === 0 ? (
        <p className="text-sm text-slate-500">Niciun link încă.</p>
      ) : (
        <ul className="space-y-2">
          {links.map((l) => {
            const s = state(l);
            const active = s.label === "Activ";
            return (
              <li key={l.id} className="p-3 border rounded-xl border-slate-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${s.cls}`}>
                    {s.label}
                  </span>
                  {l.label && <span className="text-sm font-semibold text-slate-700">{l.label}</span>}
                  <span className="text-xs text-slate-400">
                    {l.uses} {l.uses === 1 ? "înscriere" : "înscrieri"}
                    {l.max_uses != null ? ` din ${l.max_uses}` : ""}
                    {l.expires_at
                      ? ` · până la ${new Date(l.expires_at).toLocaleDateString("ro-RO")}`
                      : ""}
                  </span>
                  <div className="flex gap-1 ml-auto">
                    {active && (
                      <button
                        type="button"
                        onClick={() => copy(l.token)}
                        className="px-3 py-1.5 text-xs font-semibold text-indigo-600 rounded-lg hover:bg-indigo-50"
                      >
                        Copiază
                      </button>
                    )}
                    {!l.revoked_at && (
                      <button
                        type="button"
                        onClick={() => revoke(l)}
                        className="px-3 py-1.5 text-xs font-semibold text-slate-400 rounded-lg hover:text-rose-600"
                      >
                        Dezactivează
                      </button>
                    )}
                  </div>
                </div>
                {active && (
                  <p className="mt-2 text-xs break-all text-slate-500">{publicUrl(l.token)}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
